import { Helmet } from "react-helmet-async";
import { canonicalUrl, isLeesHost } from "./hosts";

/**
 * HeadTags — mount once per page to inject title/meta/canonical/OG/Twitter/JSON-LD.
 *
 * Props:
 *   title           — <title>
 *   description     — meta description
 *   path            — pathname this page lives at (e.g. "/", "/hvac", "/legacy")
 *   image           — OG/Twitter preview image URL (absolute)
 *   type            — og:type (default "website")
 *   twitterCard     — twitter:card (default "summary_large_image")
 *   schemas         — array of JSON-LD objects to embed
 *   robots          — e.g. "noindex,nofollow"; omit for default indexable
 *   siteName        — optional brand site name for OG
 *   locale          — optional og:locale (default "en_US")
 *   canonical       — optional absolute canonical URL (overrides path-based canonical)
 */
export default function HeadTags({
  title,
  description,
  path = "/",
  image,
  type = "website",
  twitterCard = "summary_large_image",
  schemas = [],
  robots,
  siteName,
  locale = "en_US",
  favicon,
  canonical: canonicalOverride,
}) {
  const canonical = canonicalOverride || canonicalUrl(path);
  const brandSiteName =
    siteName || (isLeesHost() ? "Lee's Heating & Cooling" : "S.S. Cleaning Solutions");

  return (
    <Helmet prioritizeSeoTags>
      {title ? <title>{title}</title> : null}
      {description ? <meta name="description" content={description} /> : null}
      {robots ? <meta name="robots" content={robots} /> : null}

      <link rel="canonical" href={canonical} />

      {favicon ? <link rel="icon" href={favicon} /> : null}
      {favicon ? <link rel="apple-touch-icon" href={favicon} /> : null}

      {/* Open Graph */}
      {title ? <meta property="og:title" content={title} /> : null}
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={brandSiteName} />
      <meta property="og:locale" content={locale} />
      {image ? <meta property="og:image" content={image} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? <meta name="twitter:description" content={description} /> : null}
      {image ? <meta name="twitter:image" content={image} /> : null}

      {schemas.map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
